import { useState, useCallback } from 'react';
import { useSubscription } from '@/contexts/billing/subscription';
import { SubscriptionService } from '@/services/billing/SubscriptionService';
import { queryClient } from '@/lib/queryClient';

export const useSubscriptionUpdate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { invalidateAndRefetch } = useSubscription();

  const updateQuantity = useCallback(async (newQuantity: number) => {
    setLoading(true);
    setError(null);

    try {
      const data = await SubscriptionService.updateSubscriptionQuantity(newQuantity);
      invalidateAndRefetch();
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [invalidateAndRefetch]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    updateQuantity,
    loading,
    error,
    clearError
  };
};

export const usePurchaseAgentCalls = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const purchaseCalls = useCallback(async (agentId: string, quantity: number) => {
    setLoading(true);
    setError(null);

    try {
      const data = await SubscriptionService.purchaseAgentCalls(agentId, quantity);

      queryClient.invalidateQueries({ queryKey: ['agents'] });
      queryClient.invalidateQueries({ queryKey: ['user-agents'] });

      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    purchaseCalls,
    loading,
    error,
    clearError,
  };
};